import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Slider } from "@/components/ui/slider";

// الثبات/التعبير/القرب من الأصل أُزيلت من الواجهة عمدًا: المحرّك يعمل دائمًا في الوضع الثابت (Robust) حتى تبقى الشخصية واحدة بين العيّنة والفيديو؛
// الاختلاف بين الأساليب يأتي من السرعة والمؤثرات فقط.
export const SETTINGS = [
  ["speed", "السرعة", 0.7, 1.2, 0.05, "أقل = كلام أبطأ وأوضح، أعلى = إيقاع أسرع للإعلانات الحماسية"],
];
export const FX = [
  ["pitch", "طبقة الصوت", -6, 6, 0.5, "سالب = صوت أعمق وأثقل، موجب = صوت أحدّ وأصغر"],
  ["depth", "العمق والفخامة", 0, 12, 0.5, "يقوّي الترددات المنخفضة لصوت فخم ومليان"],
  ["clarity", "الوضوح واللمعان", 0, 10, 0.5, "يبرز الحروف ويخلي الكلام أنظف على الجوال"],
  ["reverb", "صدى الاستوديو", 0, 1, 0.05, "إحساس غرفة تسجيل؛ قليل منه يعطي فخامة"],
  ["echo", "الصدى البعيد", 0, 1, 0.05, "تكرار الصوت كأنه في قاعة — استخدمه بخفة"],
  ["volume", "ارتفاع الصوت", 0.3, 2.5, 0.05, "1 = طبيعي"],
];
export const DEF_FX = { pitch: 0, depth: 0, clarity: 0, echo: 0, reverb: 0, radio: false, volume: 1 };

// iOS only lets an <audio> play from code after it was started inside a tap; prime it with silence on the tap.
export const SILENT = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
export const unlock = (a) => {
  if (!a || a.dataset.unlocked) return;
  a.src = SILENT;
  a.play().catch(() => {});
  a.dataset.unlocked = "1";
};
let _current = null;  // مشغّل واحد نشط في الصفحة كلها — بدء أي صوت يوقف السابق (لا تداخل)
export const stopAll = () => { if (_current) { try { _current.pause(); _current.currentTime = 0; } catch { /* noop */ } } };
export const playOn = (a, url) => {
  if (!a) return;
  if (_current && _current !== a) stopAll();
  _current = a;
  a.pause();
  a.src = url;
  a.play().catch(() => {});
};

export const errMsg = (err) => (err.message && !err.response ? err.message : formatError(err.response?.data?.detail));

export const waitTask = async (id, maxMs = 300000) => {
  const end = Date.now() + maxMs;
  while (Date.now() < end) {
    const { data } = await api.get(`/admin/voices/tasks/${id}`);
    if (data.status === "done") return data;
    if (data.status === "error") throw new Error(data.error || "تعذّر التنفيذ");
    await new Promise((r) => setTimeout(r, 2500));
  }
  throw new Error("انتهت المهلة، حاول مرة ثانية");
};

export const Knob = ({ k, label, min, max, step, value, help, onChange, onCommit, badge }) => (
  <div className="space-y-1.5" data-testid={`knob-${k}`}>
    <div className="flex justify-between items-center text-xs gap-2">
      <span className="font-medium">{label}{badge && <span className="mr-1.5 text-[10px] text-accent">{badge}</span>}</span>
      <span className="text-primary font-mono">{Number(value).toFixed(2)}</span>
    </div>
    <Slider dir="ltr" min={min} max={max} step={step} value={[Number(value)]} onValueChange={([x]) => onChange(x)} onValueCommit={([x]) => onCommit && onCommit(x)} />
    {help && <p className="text-[11px] text-muted-foreground leading-relaxed">{help}</p>}
  </div>
);

/** One TTS take (costs credit) + instant local FX re-renders on top of it (free). */
export const useTake = () => {
  const [rawId, setRawId] = useState(null);
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fxBusy, setFxBusy] = useState(false);
  const seq = useRef(0);
  const el = useRef(null);
  const applyFx = async (fx, rid = rawId) => {
    if (!rid) return;
    const n = ++seq.current;
    setFxBusy(true);
    try {
      const r = await api.post("/admin/voices/fx", { raw_id: rid, fx }, { responseType: "blob" });
      if (n === seq.current) { const u = URL.createObjectURL(r.data); setUrl(u); playOn(el.current, u); }
    } catch (err) { if (err.response?.status === 404) setRawId(null); toast.error("انتهت العينة — اضغط توليد من جديد"); }
    finally { if (n === seq.current) setFxBusy(false); }
  };
  const generate = async (body, fx) => {
    unlock(el.current);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/voices/preview", body);
      const st = await waitTask(data.task_id);
      setRawId(st.raw_id);
      await applyFx(fx, st.raw_id);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return { rawId, url, busy, fxBusy, generate, applyFx, el, unlock: () => unlock(el.current) };
};

export const TakePlayer = ({ take, testid }) => (
  <audio ref={take.el} controls playsInline className={take.url ? "w-full h-10" : "hidden"} data-testid={take.url ? testid : undefined} />
);
